// No imports needed

/**
 * Interface for partial failure information
 */
export interface PartialFailureInfo {
  code: string;
  message: string;
  path?: readonly (string | number)[];
  details?: unknown;
}

/**
 * Interface for partial failure error
 */
export interface PartialFailureError {
  graphQLErrors: {
    message: string;
    path?: readonly (string | number)[];
    extensions?: {
      code?: string;
      partialFailure?: boolean;
      details?: unknown;
    };
  }[];
  operation: {
    operationName?: string;
    variables?: Record<string, unknown>;
    query?: unknown;
    [key: string]: unknown;
  };
  response?: {
    data?: Record<string, unknown>;
    errors?: unknown[];
    [key: string]: unknown;
  };
}

/**
 * Extracts partial failure information from GraphQL errors
 */
export function extractPartialFailureInfo(error: PartialFailureError): PartialFailureInfo[] {
  return error.graphQLErrors.map(graphQLError => {
    const code = graphQLError.extensions?.code || 'PARTIAL_FAILURE';
    const message = graphQLError.message || 'A partial failure occurred';
    const path = graphQLError.path;
    const details = graphQLError.extensions?.details;

    return {
      code,
      message,
      path,
      details,
    };
  });
}

/**
 * Gets a user-friendly message for a partial failure
 */
export function getPartialFailureMessage(partialFailure: PartialFailureInfo): string {
  // Extract the field name from the path if available
  let fieldName = '';

  if (partialFailure.path && partialFailure.path.length > 0) {
    const lastPathSegment = partialFailure.path[partialFailure.path.length - 1];

    fieldName = typeof lastPathSegment === 'string' ? lastPathSegment : '';
  }

  // Create a user-friendly message based on the field name
  if (fieldName === 'generatedPrompts') {
    return `Community created successfully, but prompt generation failed: ${partialFailure.message}`;
  }

  return partialFailure.message;
}

// Store for partial failures
let partialFailures: PartialFailureInfo[] = [];
let listeners: ((failures: PartialFailureInfo[]) => void)[] = [];

/**
 * Adds a partial failure to the store
 */
export function addPartialFailure(failure: PartialFailureInfo): void {
  partialFailures = [...partialFailures, failure];
  notifyListeners();
}

/**
 * Clears all partial failures
 */
export function clearPartialFailures(): void {
  partialFailures = [];
  notifyListeners();
}

/**
 * Gets all partial failures
 */
export function getPartialFailures(): PartialFailureInfo[] {
  return [...partialFailures];
}

/**
 * Subscribes to partial failure changes
 * @returns A function to unsubscribe
 */
export function subscribeToPartialFailures(listener: (failures: PartialFailureInfo[]) => void): () => void {
  listeners.push(listener);

  // Notify the listener of the current state
  listener(partialFailures);

  // Return unsubscribe function
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

/**
 * Notifies all listeners of changes
 */
function notifyListeners(): void {
  listeners.forEach(listener => {
    listener(partialFailures);
  });
}

/**
 * Handles partial failures from GraphQL operations
 */
export function handlePartialFailure(error: PartialFailureError): void {
  const failures = extractPartialFailureInfo(error);

  failures.forEach(failure => {
    console.warn('Partial failure:', failure);
    addPartialFailure(failure);
  });
}
